import * as React from 'react';
import {
  Drills,
  ExerciseType
} from '../Drills/Drills';
import LazyDrillLoader from './LazyDrillLoader/LazyDrillLoader';
import Header from '../Header/Header';
import Footer from '../Footer/Footer';
import styled from '@emotion/styled';

interface DrillSelectorState {
  hasChangedExercise: boolean;
  selectedExercise: ExerciseType;
  selectedExerciseNumber: number;
  selectedExerciseSubCategory: string;
}

const DrillContent = styled('div')`
  flex-grow: 1;
  padding: 2rem 3rem;
  background: white;

  @media (max-width: 1024px) {
    padding: 1rem;

    img {
      max-width: 100%;
    }
  }
`;

const SelectContainer = styled('div')`
  width: 100%;
`;

const DrillSelect = styled('select')`
  // appearance: none;
  outline: none;
  padding: 0.5rem 1rem;
  margin: 0 2rem 0 0;
  font-size: 1.25rem;
  font-weight: 700;

  @media (max-width: 1024px) {
    display: block;
    margin: 0 0 1rem 0;
    width: 100%;
    max-width: 400px;
  }
`;

/**
 * 
 * @class DrillSelector
 * @returns JSX.Element
 */
class DrillSelector extends React.Component<{}, DrillSelectorState> {
  constructor(props: any) {
    super(props);

    this.state = {
      hasChangedExercise: false,
      selectedExercise: ExerciseType['RUGBY_UNION'],
      selectedExerciseNumber: 0,
      selectedExerciseSubCategory: ''
    };

    this.drillSelectionOnChange = this.drillSelectionOnChange.bind(this);
    this.exerciseSelectionOnChange = this.exerciseSelectionOnChange.bind(this);
  }

  /**
   * Updates the selected set of exercises, resets previous selections and
   * displays the content.
   *
   * @param {React.ChangeEvent<HTMLSelectElement>} event 
   * @returns void
   */
  drillSelectionOnChange (event: React.ChangeEvent<HTMLSelectElement>): void {
    // Absolutely disgusting.
    let enumKey: ExerciseType = ExerciseType[event.currentTarget.value as keyof typeof ExerciseType];

    this.setState({
      hasChangedExercise: true,
      selectedExercise: enumKey,
      selectedExerciseNumber: 0
    });
  }

  /**
   * Adds a subtitle to the content, and sets the exercise to lazy load.
   *
   * @param {React.ChangeEvent<HTMLSelectElement>} event 
   * @returns void
   */
  exerciseSelectionOnChange (event: React.ChangeEvent<HTMLSelectElement>): void {
    let subCategory = '';

    // Things that are infuriating for 400, Alex.
    // ! suppresses type null warnings.
    if (
      event.currentTarget.selectedOptions.length > 0 &&
      event.currentTarget.selectedOptions.item(0) &&
      event.currentTarget.selectedOptions.item(0)!.parentElement!.localName === 'optgroup' &&
      event.currentTarget.selectedOptions.item(0)!.parentElement!.hasAttribute('label')
    ) {
      subCategory = event.currentTarget.selectedOptions.item(0)!.parentElement!.getAttribute('label') || '';
    }

    this.setState({
      selectedExerciseNumber: parseInt(event.currentTarget.value),
      selectedExerciseSubCategory: subCategory,
    });
  }

  /**
   * Create a select component with all of the choices for sport types.
   *
   * @returns JSX.Element
   */
  listDrillTypes () {
    const components = [];

    for (let exercise in ExerciseType) {
      if (isNaN(Number(exercise))) {
        components.push(
          <option
            key={exercise}
            value={exercise}
          >
            { ExerciseType[exercise] }
          </option>
        );
      }
    }

    return (
      <DrillSelect
        onChange={ this.drillSelectionOnChange }
        defaultValue="0"
      >
        <option value="0" disabled>
          Select your Drill Type
        </option>
        { components }
      </DrillSelect>
    );
  }

  /**
   * Create a select component with all of the drills available.
   * 
   * @returns JSX.Element
   */
  listExercises () {
    let components = [];
    let numberOfExercises = Drills[this.state.selectedExercise].length;
    let optionGroup = {
      title: '',
      items: new Array()
    };
    
    for (let i = 0; i < numberOfExercises; i++) {
      let selected = Drills[this.state.selectedExercise][i];
      let key = `${i}_${String.prototype.toLowerCase.call(this.state.selectedExercise.replace(' ', '_'))}`;

      if (selected.optGroup === '') {
        components.push(<option
          value={i}
          key={key}
        >
          {selected.title}
        </option>);
      } else {
        // If the option group is already set, then just add to it.
        if (selected.optGroup === optionGroup.title) {
          optionGroup.items.push(
            <option
              value={i}
              key={key}
            >
              {selected.title}
            </option>
          );
        // ... else it's not the same as any existing option group, and the previous
        // one needs to be output and our component reset.
        } else {
          if (optionGroup.items.length > 0) {
            components.push(
              <optgroup 
                key={key}
                label={optionGroup.title}
              >
                { optionGroup.items }
              </optgroup>
            );
          }

          optionGroup.title = selected.optGroup;
          optionGroup.items = [];
          optionGroup.items.push(
            <option
              value={i}
              key={key}
            >
              {selected.title}
            </option>
          );
        }
      }

      if (i + 1 === numberOfExercises && optionGroup.items.length > 0) {
        components.push(
          <optgroup
            key={key + '_final'}
            label={optionGroup.title}
          >
            { optionGroup.items }
          </optgroup>
        );
      }
    }

    return (
      <DrillSelect
        onChange={ this.exerciseSelectionOnChange }
        defaultValue={ this.state.selectedExerciseNumber + '' }
      >
        { components }
      </DrillSelect>
    );
  }

  render () {
    return (
      <DrillContent>
        {
          !this.state.hasChangedExercise &&
          <p>Start by selecting the type of sport or institution below</p>
        }
        <SelectContainer>
          {
            /**
             * Output all of the selectable types of drills available.
             */
            this.listDrillTypes()
          }
          {
            /**
             * List all of the exercises from the Drills component.
             */
            this.state.hasChangedExercise &&
            this.listExercises()
          }
        </SelectContainer>
        {
          /**
           * Output 
           */
          this.state.selectedExerciseSubCategory &&
          <h3>{ this.state.selectedExerciseSubCategory }</h3> 
        }
        {
          /**
           * 
           */
          this.state.hasChangedExercise &&
          <React.Suspense fallback={<p>Loading...</p>}>
            <LazyDrillLoader
              type={this.state.selectedExercise}
              number={this.state.selectedExerciseNumber}
              drills={Drills}
            />
          </React.Suspense>
        }
      </DrillContent>
    )
  }
};

export default DrillSelector;
